/**
 * Utilidades de SEO tecnico para el <head>.
 *
 * La web es una SPA sin SSR: todas las etiquetas de SEO se inyectan en cliente
 * desde `router.beforeEach`. Este modulo concentra esa manipulacion para que el
 * router solo se ocupe del enrutado.
 *
 * Dos ideas centrales:
 *
 * 1. Las URLs canonicas se *construyen* a partir de (idioma, nombre de ruta, id)
 *    en lugar de leerse de `location`. Asi el canonical no arrastra parametros de
 *    tracking ni fragmentos, y es identico al hreflang propio de la pagina.
 * 2. El dominio canonico es fijo (no `window.location.origin`), de modo que PRE y
 *    desarrollo apuntan a produccion y no se indexan por su cuenta.
 */
import messages from '@/translations';

/** Dominio canonico oficial: sin `www` y con prefijo de idioma. */
export const CANONICAL_ORIGIN = 'https://ajusa.online';

/**
 * Idiomas realmente traducidos. Se derivan de `translations` a proposito: el
 * regex `:lang` del router admite mas idiomas de los que existen (p. ej. `ru`,
 * que no esta traducido). Derivandolos, el dia que se anada un idioma nuevo su
 * hreflang aparece solo sin tocar este fichero.
 */
export const LANGUAGES = Object.keys(messages);

/** Idioma al que se redirige cuando la URL pide un idioma sin traducir. */
export const FALLBACK_LANGUAGE = 'en';

/**
 * Idioma cuyo slug se usa para la URL de `x-default` (la version sin prefijo de
 * idioma, que hace deteccion automatica en `router.beforeEach`).
 */
const X_DEFAULT_LANGUAGE = 'es';

/** Nombre del sitio en `og:site_name`. */
const SITE_NAME = 'AJUSA';

/**
 * Paginas bloqueadas en public/robots.txt, por idioma y nombre de ruta.
 *
 * Si Google no puede rastrear una URL, no debe declararse como version
 * traducida de otra ni aparecer en el sitemap: seria pedirle que indexe algo
 * que a la vez se le prohibe leer. Mantener esta lista sincronizada con
 * robots.txt.
 */
const ROBOTS_DISALLOWED = [
  { lang: 'us', routeName: 'aniversario50' },
  { lang: 'us', routeName: 'sala_prensa' },
];

/**
 * Rutas que se rastrean pero no se indexan: `noindex, follow`.
 *
 * Son las dos plantillas del blog -ficha de entrada y pagina de categoria-. El
 * cliente quiere que Google deje de indexarlas y de evaluar su calidad, pero
 * que siga los enlaces internos que llevan a la web corporativa, de modo que no
 * se pierda ese enlazado: por eso `follow` y no `nofollow`.
 *
 * El indice del blog (`blog`) queda fuera a proposito: sigue siendo indexable.
 *
 * Al ser una lista de nombres de ruta, cubre de una vez todas las entradas y
 * categorias existentes y las que se publiquen en el futuro.
 *
 * Importante: estas rutas NO deben anunciarse en el sitemap. Ver
 * `build/generate-sitemap.js`.
 */
export const NOINDEX_ROUTES = ['blog_noticia', 'blog_categoria'];

/**
 * Nombre de la ruta comodin que atiende las URLs que no existen.
 *
 * Hasta ahora no habia ninguna: `error-404.vue` estaba en el repositorio pero
 * sin registrar, de modo que cualquier URL invalida renderizaba la portada y
 * respondia 200. Era un *soft 404* en todo el sitio.
 */
export const NOT_FOUND_ROUTE = 'error_404';

/**
 * Rutas cuyo `:id?` NO identifica una pagina distinta, sino una seccion dentro
 * de la misma.
 *
 * `productos.vue`, `catalogo.vue` y `area-tecnica.vue` usan `params.id` solo
 * para hacer `scrollTo`: el contenido, el titulo y la descripcion son identicos
 * con id y sin el. Funciona como un ancla, y por eso la URL canonica se
 * construye SIN id.
 *
 * Sin esto, cada ancla declaraba su propia URL canonica -`/es/area-tecnica/tips`,
 * `/es/area-tecnica/webinarios`...-, de modo que la misma pagina entraba varias
 * veces en el indice con los mismos metadatos y repartia su autoridad. Peor
 * aun: cualquier id inventado generaba una URL indexable
 * (`/es/catalogo/lo-que-sea` se autocanonicalizaba).
 *
 * `product`, `blog_noticia` y `blog_categoria` NO estan aqui: su id si carga
 * contenido propio.
 */
const ANCHOR_ID_ROUTES = ['productos', 'catalogo', 'area_tecnica'];

/** ¿El `:id?` de esta ruta es solo un ancla de navegacion? */
export function isAnchorIdRoute(routeName) {
  return ANCHOR_ID_ROUTES.indexOf(routeName) !== -1;
}

/**
 * Id con el que deben construirse las senales de SEO (canonical, hreflang,
 * og:url, JSON-LD): `null` cuando el id es un ancla.
 */
export function canonicalIdFor(routeName, id) {
  return isAnchorIdRoute(routeName) ? null : id;
}

/** ¿Esta pagina se rastrea pero no se indexa? */
export function isNoindexRoute(routeName) {
  return NOINDEX_ROUTES.indexOf(routeName) !== -1;
}

/** ¿Esta esta pagina bloqueada por robots.txt? */
export function isDisallowedByRobots(lang, routeName) {
  for (let i = 0; i < ROBOTS_DISALLOWED.length; i++) {
    if (ROBOTS_DISALLOWED[i].lang === lang && ROBOTS_DISALLOWED[i].routeName === routeName) {
      return true;
    }
  }
  return false;
}

/** Dimensiones reales de las imagenes que genera build/generate-og-images.js. */
const OG_IMAGE_WIDTH = 1200;
const OG_IMAGE_HEIGHT = 630;

const OG_IMAGE_DIR = '/static/og/';

/** Imagen usada por las paginas sin una propia (legales, mapa web, gracias...). */
const OG_IMAGE_DEFAULT = 'og-default.jpg';

/**
 * Imagen representativa por nombre de ruta. Los ficheros los genera
 * `build/generate-og-images.js` (ver ese fichero para las fuentes).
 *
 * Las rutas ausentes caen en `OG_IMAGE_DEFAULT`. El blog queda fuera a
 * proposito: no entra en el alcance del trabajo de SEO.
 */
const OG_IMAGE_BY_ROUTE = {
  home: 'og-home.jpg',
  sobre_nosotros: 'og-default.jpg',
  aniversario50: 'og-historia.jpg',
  vehiculo_electrico: 'og-vehiculo-electrico.jpg',
  area_tecnica: 'og-area-tecnica.jpg',
  productos: 'og-productos.jpg',
  productos_quimicos: 'og-productos-quimicos.jpg',
  catalogo: 'og-catalogo.jpg',
  contacto: 'og-contacto.jpg',
  sala_prensa: 'og-sala-prensa.jpg',
  trabaja_con_nosotros: 'og-trabaja-con-nosotros.jpg',
  actualidad: 'og-actualidad.jpg',
};

/**
 * `og:type` por ruta. El resto de paginas son `website`.
 *
 * Antes index.html declaraba `article` para todo el sitio, incluida la home.
 */
const OG_TYPE_BY_ROUTE = {
  blog_noticia: 'article',
  actualidad: 'article',
};

/**
 * Rutas cuyo `:id?` es un slug traducido, y seccion de `translations` que
 * contiene el `nav_options` con la correspondencia por idioma.
 *
 * Solo estas rutas pueden emitir hreflang cuando llevan id: para las demas no
 * hay forma de saber la URL equivalente en otro idioma.
 */
const ID_SECTION_BY_ROUTE = {
  product: 'productos',
  catalogo: 'catalogo',
  area_tecnica: 'area_tecnica',
};

/**
 * Indice bidireccional id <-> clave canonica, por seccion e idioma.
 *
 * Se construye desde `nav_options` y NO desde `productos.keys` (el mapa inverso
 * que ya existe en translations) porque este ultimo esta desincronizado:
 * en `br` falta la entrada de `juntas-cabeçote` y en `us` la de
 * `chemical-products-line`. Derivarlo aqui hace que el hreflang funcione
 * tambien en esas dos paginas.
 */
function buildIdIndex() {
  const index = {};

  Object.keys(ID_SECTION_BY_ROUTE).forEach(function (routeName) {
    const section = ID_SECTION_BY_ROUTE[routeName];
    if (index[section]) return;

    const byId = {};
    const byKey = {};

    LANGUAGES.forEach(function (lang) {
      byId[lang] = {};
      byKey[lang] = {};

      const sectionData = messages[lang] ? messages[lang][section] : null;
      const navOptions = sectionData ? sectionData.nav_options : null;
      if (!navOptions) return;

      Object.keys(navOptions).forEach(function (key) {
        const id = navOptions[key] ? navOptions[key].id : null;
        // Hay entradas de navegacion sin `id` (enlaces a otras vistas o externos).
        if (typeof id !== 'string' || id === '') return;
        byId[lang][id] = key;
        byKey[lang][key] = id;
      });
    });

    index[section] = { byId: byId, byKey: byKey };
  });

  return index;
}

const idIndex = buildIdIndex();

/** ¿Existe traduccion para este idioma? */
export function isTranslatedLanguage(lang) {
  return LANGUAGES.indexOf(lang) !== -1;
}

/**
 * Clave canonica de un id dentro de su seccion, o `null` si no se reconoce.
 * Es lo que permite saltar de `/es/producto/junta-culata` a
 * `/en/product/cylinder-head-gaskets`.
 */
export function getCanonicalKey(routeName, id, lang) {
  const section = ID_SECTION_BY_ROUTE[routeName];
  if (!section || !id) return null;

  const byId = idIndex[section].byId[lang];
  return byId && byId[id] ? byId[id] : null;
}

/** Traduce un id de ruta entre idiomas. Devuelve `null` si no es posible. */
export function translateId(routeName, id, fromLang, toLang) {
  if (!id) return null;
  if (fromLang === toLang) return id;

  const section = ID_SECTION_BY_ROUTE[routeName];
  if (!section) return null;

  const key = getCanonicalKey(routeName, id, fromLang);
  if (!key) return null;

  const byKey = idIndex[section].byKey[toLang];
  return byKey && byKey[key] ? byKey[key] : null;
}

/**
 * Ruta absoluta (sin dominio) de una pagina. `null` si el idioma no tiene slug
 * para esa ruta (ocurre: `bonos_ajusa` no existe en `us`).
 *
 * El formato replica el del sitemap actual: la home lleva barra final
 * (`/es/`) y el resto no (`/es/productos`).
 */
export function buildPath(lang, routeName, id) {
  const langMessages = messages[lang];
  const enlaces = langMessages ? langMessages.enlaces : null;
  const slug = enlaces ? enlaces[routeName] : null;

  if (slug == null || slug === '') return null;

  // La home tiene el slug '/': evita generar '/es//'.
  if (slug === '/') return '/' + lang + '/';

  // `enlaces` no solo contiene slugs de pagina: tambien URLs externas
  // (`catalogo_online`, `online_store`), rutas a PDF (`certificado_*`,
  // `condiciones_venta`) y slugs con fragmento (`solicite_catalogo`). Ninguna es
  // una pagina del router, asi que no puede formar parte de una URL canonica.
  if (slug.indexOf('://') !== -1 || slug.charAt(0) === '/' || slug.indexOf('#') !== -1) {
    return null;
  }

  let path = '/' + lang + '/' + slug;
  if (id) path += '/' + id;
  return path;
}

/**
 * Codificacion porcentual de un path, segmento a segmento.
 *
 * Los slugs de `translations` llevan el caracter sin codificar (`juntas-cabecote`
 * se almacena con `c` cedilla literal), pero el sitemap y los enlaces que Google
 * guarda usan la forma codificada. Si el canonical declara una y el sitemap la
 * otra, una auditoria las lee como URLs distintas.
 *
 * Es idempotente a proposito: decodifica antes de codificar, de modo que un
 * segmento que ya llegue codificado no se escape dos veces (`%C3%A7` no se
 * convierte en `%25C3%25A7`).
 */
function encodePath(path) {
  return path
    .split('/')
    .map(function (segment) {
      if (segment === '') return segment;
      let raw = segment;
      try {
        raw = decodeURIComponent(segment);
      } catch (e) {
        // Segmento con un `%` que no forma una secuencia valida: se deja crudo.
        raw = segment;
      }
      return encodeURIComponent(raw);
    })
    .join('/');
}

/** URL canonica absoluta de una pagina, o `null` si no se puede construir. */
export function buildUrl(lang, routeName, id) {
  const path = buildPath(lang, routeName, id);
  return path ? CANONICAL_ORIGIN + encodePath(path) : null;
}

/**
 * URL de `x-default`: la version sin prefijo de idioma. El `:lang?` opcional del
 * router hace que estas URLs resuelvan y redirijan al idioma detectado, que es
 * justo el comportamiento que `x-default` debe senalar.
 *
 * Es la misma para todo el grupo de traducciones (usa el slug de un idioma de
 * referencia), como exige la reciprocidad de hreflang.
 */
function buildXDefaultUrl(routeName, id, currentLang) {
  const referenceId = id ? translateId(routeName, id, currentLang, X_DEFAULT_LANGUAGE) : null;
  if (id && !referenceId) return null;

  const path = buildPath(X_DEFAULT_LANGUAGE, routeName, referenceId);
  if (!path) return null;

  // Quita el prefijo de idioma: '/es/productos' -> '/productos', '/es/' -> '/'.
  const withoutLang = path.replace('/' + X_DEFAULT_LANGUAGE, '');
  return CANONICAL_ORIGIN + encodePath(withoutLang === '' ? '/' : withoutLang);
}

/**
 * Alternates hreflang de la pagina actual, con la URL *equivalente* de cada
 * idioma (no la home, como estaba hardcodeado en index.html).
 *
 * Devuelve una lista vacia cuando la pagina lleva un id que no se puede
 * traducir: es preferible no emitir senal alguna que declarar como equivalente
 * una URL que no lo es o que responderia 404.
 */
export function getAlternateUrls(routeName, currentLang, id) {
  if (id) {
    if (!ID_SECTION_BY_ROUTE[routeName]) return [];
    if (!getCanonicalKey(routeName, id, currentLang)) return [];
  }

  const alternates = [];

  LANGUAGES.forEach(function (lang) {
    // Bloqueada en robots.txt: no se declara como version traducida.
    if (isDisallowedByRobots(lang, routeName)) return;

    const translatedId = id ? translateId(routeName, id, currentLang, lang) : null;
    // Hueco de datos en ese idioma: se omite en lugar de inventar la URL.
    if (id && !translatedId) return;

    const url = buildUrl(lang, routeName, translatedId);
    if (!url) return;

    alternates.push({ hreflang: messages[lang].isotag, href: url });
  });

  // `x-default` solo tiene sentido si hay mas de una version.
  if (alternates.length > 1) {
    const xDefault = buildXDefaultUrl(routeName, id, currentLang);
    if (xDefault) alternates.push({ hreflang: 'x-default', href: xDefault });
  }

  return alternates;
}

/**
 * Busca una etiqueta del <head> y la crea si no existe.
 *
 * El codigo anterior hacia `document.head.querySelector(sel).content = x`, que
 * lanza TypeError en cuanto la etiqueta falta en index.html. Este upsert permite
 * ademas anadir etiquetas nuevas sin tocar la plantilla.
 */
function upsertHeadTag(selector, tagName, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement(tagName);
    Object.keys(attributes).forEach(function (name) {
      element.setAttribute(name, attributes[name]);
    });
    document.head.appendChild(element);
  }

  return element;
}

/** Escribe el contenido de una <meta>, creandola si hace falta. */
export function setMetaContent(selector, tagName, attributes, content) {
  if (content == null) return;
  upsertHeadTag(selector, tagName, attributes).setAttribute('content', content);
}

/** Escribe `<link rel="canonical">`. */
export function setCanonical(url) {
  if (!url) return;
  upsertHeadTag("link[rel='canonical']", 'link', { rel: 'canonical' }).setAttribute('href', url);
}

/**
 * Reemplaza los `<link rel="alternate" hreflang="...">` del <head>.
 *
 * Se eliminan todos los previos antes de escribir los nuevos: en una SPA el
 * <head> persiste entre navegaciones, asi que sin esta limpieza se acumularian
 * los alternates de las paginas ya visitadas.
 */
export function setAlternates(alternates) {
  const previous = document.head.querySelectorAll("link[rel='alternate'][hreflang]");
  // Bucle clasico: NodeList.forEach no existe en navegadores antiguos.
  for (let i = 0; i < previous.length; i++) {
    previous[i].parentNode.removeChild(previous[i]);
  }

  alternates.forEach(function (alternate) {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', alternate.hreflang);
    link.setAttribute('href', alternate.href);
    document.head.appendChild(link);
  });
}

/**
 * Escribe o retira `<meta name="robots">` segun la ruta.
 *
 * El <head> persiste entre navegaciones, asi que **retirar la etiqueta es tan
 * importante como escribirla**: sin esto, al pasar de una entrada de blog a
 * cualquier otra pagina el `noindex` se quedaria pegado y dejaria de indexarse
 * una pagina que si debe indexarse.
 */
export function applyRobots(routeName) {
  const selector = "meta[name='robots']";

  // La 404 no lleva `follow`: no hay enlaces propios que merezca seguir.
  let content = null;
  if (routeName === NOT_FOUND_ROUTE) content = 'noindex';
  else if (isNoindexRoute(routeName)) content = 'noindex, follow';

  if (!content) {
    const previous = document.head.querySelector(selector);
    if (previous) previous.parentNode.removeChild(previous);
    return null;
  }

  setMetaContent(selector, 'meta', { name: 'robots' }, content);
  return content;
}

/**
 * Codigo de estado HTTP que debe devolver la capa de prerenderizado.
 *
 * Una SPA no puede emitir el codigo por si misma, y Apache tampoco puede
 * saber que rutas del router existen. Pero las peticiones de los bots pasan por
 * Prerender.io (comprobado: las rutas de pagina llevan cabeceras
 * `x-prerender-*`, los ficheros estaticos no), y Prerender.io lee esta etiqueta
 * y responde con ese codigo. Es lo que convierte el *soft 404* en un 404 real.
 *
 * Como el resto de etiquetas en una SPA, hay que retirarla al salir: dejarla
 * pegada haria que la siguiente pagina se sirviera como 404 a los buscadores.
 *
 * @param {string} routeName  nombre de ruta; `NOT_FOUND_ROUTE` declara 404.
 * @param {number|string} [statusCode]  codigo concreto, con prioridad sobre lo
 *   anterior. Sirve para distinguir una pagina que no existe (404) de un fallo
 *   temporal del backend (503): ver `HTTP_SERVICE_UNAVAILABLE`.
 */
export function applyPrerenderStatus(routeName, statusCode) {
  const selector = "meta[name='prerender-status-code']";

  let code = null;
  if (statusCode) code = String(statusCode);
  else if (routeName === NOT_FOUND_ROUTE) code = '404';

  if (!code) {
    const previous = document.head.querySelector(selector);
    if (previous) previous.parentNode.removeChild(previous);
    return null;
  }

  setMetaContent(selector, 'meta', { name: 'prerender-status-code' }, code);
  return code;
}

/** La pagina existe, pero ahora mismo no se puede servir. */
export const HTTP_SERVICE_UNAVAILABLE = 503;

/** La pagina no existe. */
export const HTTP_NOT_FOUND = 404;

/**
 * Retira canonical y hreflang.
 *
 * Una URL que no existe no debe declararse canonica ni anunciarse como version
 * traducida de nada. Sin esto heredaria las etiquetas de la pagina anterior.
 */
export function clearCanonicalAndAlternates() {
  const canonical = document.head.querySelector("link[rel='canonical']");
  if (canonical) canonical.parentNode.removeChild(canonical);
  setAlternates([]);
}

/**
 * Aplica canonical + hreflang de una navegacion y devuelve la URL canonica
 * (que reutiliza `og:url` para que ambas coincidan).
 */
export function applyCanonicalAndAlternates(routeName, lang, id) {
  // Un id que solo es un ancla no forma parte de la URL canonica.
  const canonicalId = canonicalIdFor(routeName, id);
  const canonicalUrl = buildUrl(lang, routeName, canonicalId);

  setCanonical(canonicalUrl);
  setAlternates(getAlternateUrls(routeName, lang, canonicalId));

  return canonicalUrl;
}

/**
 * URL absoluta de la imagen de Open Graph de una pagina.
 *
 * Las fichas de producto usan la imagen de su categoria, resuelta con el mismo
 * indice de ids que el hreflang, de modo que funciona en los 5 idiomas.
 */
export function getOgImageUrl(routeName, lang, id) {
  let file = OG_IMAGE_BY_ROUTE[routeName];

  if (routeName === 'product') {
    const key = getCanonicalKey(routeName, id, lang);
    // Sin id (o con uno desconocido) se cae al indice de productos.
    file = key ? 'og-product-' + key + '.jpg' : OG_IMAGE_BY_ROUTE.productos;
  }

  return CANONICAL_ORIGIN + OG_IMAGE_DIR + (file || OG_IMAGE_DEFAULT);
}

/**
 * Escribe las etiquetas de Open Graph y Twitter Card de una navegacion.
 *
 * `og:title`, `og:description` y `og:url` los sigue escribiendo el router junto
 * al resto de metadatos (dependen de `enlaces.seo`); aqui se resuelve todo lo
 * que se deduce de la ruta: imagen, tipo e idioma.
 */
export function applyOpenGraph(routeName, lang, id, options) {
  const settings = options || {};
  const imageUrl = getOgImageUrl(routeName, lang, id);

  setMetaContent("[property='og:site_name']", 'meta', { property: 'og:site_name' }, SITE_NAME);
  setMetaContent("[property='og:type']", 'meta', { property: 'og:type' }, OG_TYPE_BY_ROUTE[routeName] || 'website');

  setMetaContent("[property='og:image']", 'meta', { property: 'og:image' }, imageUrl);
  // `og:image:width/height` permiten a las redes maquetar la tarjeta sin haber
  // descargado antes la imagen.
  setMetaContent("[property='og:image:width']", 'meta', { property: 'og:image:width' }, String(OG_IMAGE_WIDTH));
  setMetaContent("[property='og:image:height']", 'meta', { property: 'og:image:height' }, String(OG_IMAGE_HEIGHT));
  if (settings.imageAlt) {
    setMetaContent("[property='og:image:alt']", 'meta', { property: 'og:image:alt' }, settings.imageAlt);
  }

  // og:locale usa guion bajo (es_ES), no guion, a diferencia de hreflang.
  const isotag = messages[lang] ? messages[lang].isotag : null;
  if (isotag) {
    setMetaContent("[property='og:locale']", 'meta', { property: 'og:locale' }, isotag.replace('-', '_'));
  }

  setOgLocaleAlternates(lang);

  // Twitter/X cae a las etiquetas og:* si no encuentra las suyas, pero
  // declararlas da control sobre el formato de la tarjeta.
  setMetaContent("[name='twitter:card']", 'meta', { name: 'twitter:card' }, 'summary_large_image');
  setMetaContent("[name='twitter:image']", 'meta', { name: 'twitter:image' }, imageUrl);
  if (settings.title) {
    setMetaContent("[name='twitter:title']", 'meta', { name: 'twitter:title' }, settings.title);
  }
  if (settings.description) {
    setMetaContent("[name='twitter:description']", 'meta', { name: 'twitter:description' }, settings.description);
  }

  return imageUrl;
}

/**
 * Declara los demas idiomas disponibles en `og:locale:alternate`.
 *
 * Igual que con los hreflang, se limpian los previos: son varias etiquetas con
 * la misma propiedad y en una SPA se acumularian entre navegaciones.
 */
function setOgLocaleAlternates(currentLang) {
  const previous = document.head.querySelectorAll("meta[property='og:locale:alternate']");
  for (let i = 0; i < previous.length; i++) {
    previous[i].parentNode.removeChild(previous[i]);
  }

  LANGUAGES.forEach(function (lang) {
    if (lang === currentLang) return;

    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:locale:alternate');
    meta.setAttribute('content', messages[lang].isotag.replace('-', '_'));
    document.head.appendChild(meta);
  });
}



// WEBPACK FOOTER //
// ./src/seo/head.js